<script>
/**
 * TaskClientPicker — single-select cliente vinculado à task (`pm_task.id_cliente_app`).
 *
 * Espelha visualmente o `TaskAssigneePicker` (avatar/iniciais como activator,
 * popover com busca, opção destacada em verde quando selecionada), mas:
 *  - Lê apenas `assigneesClients` (tipo='cliente') da store; ignora profissionais.
 *  - Sempre `single` — backend tem FK única `id_cliente_app`.
 *  - Emit `input` retorna o objeto selecionado `{ id, nome, avatarUrl }` (ou `null`
 *    quando "Remover vínculo" é clicado). Diferente do assignee, que emite array.
 *
 * Props:
 *  - value: Object | null — cliente atual `{ id, nome, avatarUrl }`
 *  - label: String — opcional, label acima do trigger
 *  - placeholder: String — default 'Vincular cliente'
 *  - disabled: Boolean
 *
 * Emits:
 *  - input: Object | null
 */
// Helpers compartilhados com o getter que decora a carteira uma única vez, para
// trigger e lista não divergirem. Diferença herdada: a semente de cor de cliente
// SEM nome passou de `String(id)` para `id + ':cliente'` — cliente com nome (o
// caso normal) mantém exatamente a mesma cor, porque a semente é o nome.
import {
  personName as clientName,
  personAvatar as clientAvatar,
  personBgColor as clientBgColor,
  personTextColor as clientTextColor,
  personInitials,
} from '@modules/tarefas/utils/personDecoration'

function normalize(str) {
  if (!str) return ''
  return String(str)
    .normalize('NFD')
    .replaceAll(/[̀-ͯ]/g, '')
    .toLowerCase()
}

// Renderização incremental da lista do dropdown. Mesmo tamanho de página do
// `select-search` (`itemsPerLoad: 50`), pela mesma razão: uma carteira de
// escritório passa de mil clientes e renderizar tudo na abertura é o custo
// dominante do menu.
const LIST_PAGE_SIZE = 50
// Distância (px) do fim da lista em que a janela cresce.
const LIST_GROW_THRESHOLD = 120
// Intervalo mínimo entre dois crescimentos — ver a nota em `onListScroll`.
const LIST_GROW_MIN_INTERVAL_MS = 150

export default {
  name: 'TaskClientPicker',
  props: {
    value: {
      type: Object,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Vincular cliente',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Quando false, o trigger mostra só o avatar (com tooltip do nome no
     * hover) — usado em surfaces apertadas como a linha da lista
     * (task-item.vue), seguindo o padrão de responsável/participantes. No
     * modal de detalhe (TaskFieldStacked) mantém true para o usuário ver
     * o nome sem hover.
     */
    showName: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true,
    },
    /**
     * Quando true, o estado vazio mostra apenas o ícone — sem o texto do
     * `placeholder`. Mesma motivação do `iconOnly` do TaskAssigneePicker: nas
     * linhas de tarefa/subtarefa o header já rotula a coluna.
     */
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menuOpen: false,
      search: '',
      // Janela de render da lista. Abrir o menu renderizava a carteira inteira do
      // escritório (medido: 689 opções, 3.558 nós de DOM numa abertura). Cresce ao
      // rolar até o fim da lista.
      visibleCount: LIST_PAGE_SIZE,
    }
  },
  computed: {
    clients() {
      // Carteira JÁ decorada, compartilhada por todos os pickers (ver
      // `assigneesDecoratedClients` em store/getters.js). `filteredClients`
      // devolve um SUBCONJUNTO destas referências, nunca cópias.
      return this.$store.getters['tarefas/assigneesDecoratedClients']
    },
    loading() {
      return this.$store.getters['tarefas/assigneesLoading']
    },
    selectedId() {
      return this.value && this.value.id != null ? this.value.id : null
    },
    filteredClients() {
      const base = this.clients
      if (!this.search || !this.search.length) return base
      const q = normalize(this.search)
      return base.filter(function (c) {
        return normalize(c.nome).includes(q)
      })
    },
    // Janela renderizada. `filteredClients` continua sendo o conjunto-verdade
    // (contagem, empty-state, teto do crescimento).
    visibleClients() {
      return this.filteredClients.slice(0, this.visibleCount)
    },
    hasSelection() {
      return !!(this.value && this.value.id != null)
    },
    // View-models computed from the input (value / filteredClients) so the
    // template has zero function calls in interpolations / :style / v-if (AC-M4).
    decoratedValue() {
      const client = this.value
      if (!client) return null
      const avatar = clientAvatar(client)
      const nome = clientName(client)
      return {
        nome: nome,
        initials: personInitials(nome),
        avatar: avatar,
        bgColor: avatar ? null : clientBgColor(client),
        textColor: avatar ? null : clientTextColor(client),
      }
    },
  },
  watch: {
    menuOpen(newVal) {
      if (!newVal) this.search = ''
      // Reabrir sempre começa limitado, senão a janela inflada pelo scroll da
      // abertura anterior é herdada e o menu renderiza tudo de uma vez no clique.
      this.visibleCount = LIST_PAGE_SIZE
    },
    // Busca nova = conjunto novo: a janela volta ao início, senão um filtro que
    // devolve poucos itens herdaria uma janela grande sem necessidade.
    search() {
      this.visibleCount = LIST_PAGE_SIZE
    },
  },
  created() {
    // Timestamp do último crescimento da janela. Fora do `data` de propósito: é
    // controle de throttle, não estado de render — não deve disparar reatividade.
    this._lastGrowAt = 0
  },
  methods: {
    // Cresce a janela ao chegar perto do fim da lista.
    //
    // O throttle não é decoração: um único gesto de scroll dispara dezenas de
    // eventos, e sem ele cada um somaria um passo antes do re-render — foi
    // exatamente o defeito corrigido em 967848d9a na Lista, onde rolar até o fim
    // levava 30 linhas a 750. Aqui o crescimento normal já se autolimita (cada
    // passo adiciona ~1.800px de conteúdo, jogando `remaining` muito acima do
    // limiar), mas arrastar a barra e SEGURAR no fim contorna isso; o intervalo
    // mínimo é o que impede.
    onListScroll(event) {
      const el = event && event.target
      if (!el) return
      if (this.visibleCount >= this.filteredClients.length) return

      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
      if (remaining > LIST_GROW_THRESHOLD) return

      const now = Date.now()
      if (now - this._lastGrowAt < LIST_GROW_MIN_INTERVAL_MS) return
      this._lastGrowAt = now

      this.visibleCount = Math.min(
        this.visibleCount + LIST_PAGE_SIZE,
        this.filteredClients.length
      )
    },
    pickClient(client) {
      this.$emit('input', {
        id: client.id,
        nome: client.nome,
        avatarUrl: client.avatar_url || client.avatarUrl || null,
      })
      this.menuOpen = false
    },
    clearSelection() {
      this.$emit('input', null)
      this.menuOpen = false
    },
  },
}
</script>

<template>
  <div class="task-client-picker">
    <span v-if="label" class="az-text-small text-color-600 mb-1 d-block">{{ label }}</span>

    <!-- Sem `max-height` aqui de propósito: se o v-menu for o scroller, o campo
         de busca rola junto e sai de vista. A altura passa a ser governada pela
         lista (`__list`, o único elemento com overflow), e a busca fica fixa —
         mesma estrutura do `select-search`. `max-width` porque sem ele o conteúdo
         empurrava o menu para 465px. -->
    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      offset-y
      min-width="280"
      max-width="300"
      transition="scale-transition"
      content-class="task-client-picker__menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="task-client-picker__trigger d-inline-flex align-center"
          :class="{ 'task-client-picker__trigger--empty': !hasSelection }"
          :disabled="disabled"
          :aria-label="hasSelection ? 'Alterar cliente vinculado' : 'Vincular cliente'"
          :title="hasSelection ? null : placeholder"
          v-bind="attrs"
          v-on="on"
        >
          <template v-if="!hasSelection">
            <icon-svg name="mdi-domain" :size="16" color="var(--secondary)" />
            <span v-if="!iconOnly" class="az-text-small text-color-400 ml-1">{{ placeholder }}</span>
          </template>
          <template v-else-if="decoratedValue">
            <span
              class="task-client-picker__avatar"
              :style="{
                backgroundColor: decoratedValue.bgColor,
                color: decoratedValue.textColor,
              }"
              :title="decoratedValue.nome"
            >
              <img
                v-if="decoratedValue.avatar"
                :src="decoratedValue.avatar"
                :alt="decoratedValue.initials"
                class="task-client-picker__avatar-img"
              />
              <span v-else>{{ decoratedValue.initials }}</span>
            </span>
            <span
              v-if="showName"
              class="az-text-small text-color-800 ml-2 text-truncate"
            >
              {{ decoratedValue.nome }}
            </span>
          </template>
        </button>
      </template>

      <!-- O gate `menuOpen` do bloco abaixo é LOAD-BEARING para memória — mesma
           razão do task-assignee-picker: slot default não-scoped é avaliado pelo
           render do PAI, então `decoratedClients` materializava os 689 clientes
           do escritório em cada linha da lista, com o menu fechado. -->
      <div v-if="menuOpen" class="task-client-picker__dropdown pa-2">
        <div class="px-2 py-1 az-text-small font-weight-bold text-color-500">Cliente vinculado</div>

        <div class="mb-2 px-1">
          <InputByType
            v-model="search"
            type="search"
            label=""
            placeholder="Buscar..."
            outlined
            dense
            hide-details
            class="new-ux"
          />
        </div>

        <v-progress-linear v-if="loading" indeterminate height="2" class="mb-2" />

        <div
          v-if="hasSelection && (!search || !search.length)"
          class="task-client-picker__option task-client-picker__option--clear d-flex align-center"
          @click="clearSelection"
        >
          <icon-svg name="mdi-close-circle-outline" :size="18" color="var(--secondary)" class="mr-2" />
          <span class="az-text-small text-color-700">Remover vínculo</span>
        </div>

        <div v-if="!loading && filteredClients.length === 0" class="pa-3 text-center">
          <span class="az-text-small text-color-400">Nenhum cliente encontrado</span>
        </div>

        <!-- O ÚNICO scroller do dropdown. Tudo acima (título, busca, remover
             vínculo) fica fixo. Renderiza uma janela de `visibleClients`, não a
             carteira inteira. -->
        <div class="task-client-picker__list" @scroll="onListScroll">
          <div
            v-for="client in visibleClients"
            :key="'client-' + client.id"
            class="task-client-picker__option d-flex align-center"
            :class="{
              'task-client-picker__option--selected': client.id === selectedId,
            }"
            @click="pickClient(client.raw)"
          >
            <span
              class="task-client-picker__avatar mr-2"
              :style="{ backgroundColor: client.bgColor, color: client.textColor }"
            >
              <img
                v-if="client.avatar"
                :src="client.avatar"
                :alt="client.initials"
                class="task-client-picker__avatar-img"
              />
              <span v-else>{{ client.initials }}</span>
            </span>
            <span class="az-text-small flex-grow-1 text-color-800 text-truncate">
              {{ client.nome }}
            </span>
            <icon-svg
              v-if="client.id === selectedId"
              name="mdi-check"
              :size="16"
              color="var(--primary)"
            />
          </div>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style lang="sass" scoped>
.task-client-picker
  display: inline-block

.task-client-picker__trigger
  background: transparent
  border: none
  cursor: pointer
  padding: 4px 8px
  border-radius: 4px
  transition: background-color 0.15s ease
  min-height: 32px
  max-width: 100%

  &:hover:not(:disabled)
    background-color: rgba(0, 0, 0, 0.04)

  &:disabled
    opacity: 0.6
    cursor: not-allowed

  &--empty
    padding-left: 4px

.task-client-picker__avatar
  display: inline-flex
  align-items: center
  justify-content: center
  width: 26px
  height: 26px
  border-radius: 50%
  background-color: var(--primary)
  color: var(--white)
  font-size: 10px
  font-weight: 600
  border: 2px solid var(--white)
  overflow: hidden
  flex-shrink: 0

.task-client-picker__avatar-img
  width: 100%
  height: 100%
  object-fit: cover

.task-client-picker__dropdown
  background-color: var(--white)
  min-width: 280px
  // Sem `overflow`/`max-height` aqui: era isto que fazia o campo de busca rolar
  // junto e sair de vista. O scroller é o `__list` abaixo, e tudo que vem antes
  // dele (título, busca, remover vínculo) fica fixo.
  display: flex
  flex-direction: column

.task-client-picker__list
  overflow-y: auto
  max-height: 250px
  // `contain: layout paint` isola o recálculo ao container enquanto a janela
  // cresce, em vez de refazer layout/paint da página a cada passo.
  contain: layout paint

.task-client-picker__option
  padding: 6px 8px
  cursor: pointer
  border-radius: 4px
  transition: background-color 0.15s ease

  &:hover
    background-color: rgba(0, 0, 0, 0.04)

  &--selected
    background-color: rgba(86, 175, 121, 0.08)

  &--clear
    border-bottom: 1px solid rgba(0, 0, 0, 0.06)
    margin-bottom: 4px

  .task-client-picker__avatar
    width: 24px
    height: 24px
    font-size: 10px
    border: none
</style>
